import { useCallback, useContext, useEffect } from "react";
import AuthContext from "@/react/context/authContext.jsx";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { dismissActivePageGuide, isPageGuideBlocked, replayPageGuide, startPageGuide, subscribePageGuideUnblocked } from "./pageGuide";
import type { ViewType } from "./types";

interface UseAutoPageGuideOptions {
  viewType: ViewType;
  enabled?: boolean;
  /** Re-run auto-start when page context changes (e.g. empty → manifest ready). */
  contextKey?: string;
}

export function useAutoPageGuide({ viewType, enabled = true, contextKey }: UseAutoPageGuideOptions) {
  const { userPreferences } = useSwivuBryntumContext();
  const { displayAuthForm } = useContext(AuthContext);

  useEffect(() => {
    if (!enabled || !userPreferences || displayAuthForm) return;

    let timeoutId: number | undefined;

    const scheduleStart = () => {
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (!enabled || !userPreferences || displayAuthForm || isPageGuideBlocked()) return;

      timeoutId = window.setTimeout(() => {
        void startPageGuide({ viewType, userPreferences });
      }, 500);
    };

    scheduleStart();
    const unsubscribe = subscribePageGuideUnblocked(scheduleStart);

    return () => {
      unsubscribe();
      if (timeoutId != null) window.clearTimeout(timeoutId);
      dismissActivePageGuide();
    };
  }, [contextKey, displayAuthForm, enabled, userPreferences, viewType]);

  const replayGuide = useCallback(() => {
    if (!userPreferences) return;
    void replayPageGuide(viewType, userPreferences);
  }, [userPreferences, viewType]);

  return { replayGuide };
}
